import React, { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { gcsUploadFile, gcsDeleteFile, gcsGetSignedDownloadUrl } from '@/lib/gcsStorage';
import { useAuth } from '@/contexts/AuthContext';
import { sendNotification } from '@/lib/notifications';
import { useScheduleData } from '@/hooks/useScheduleData';
import { ToolHeader } from '@/components/ToolHeader';
import { SharedNavbar } from '@/components/SharedNavbar';
import { GlobalToolsExpandingPanel } from '@/components/GlobalToolsExpandingPanel';
import { Button } from '@/components/ui/button';
import { PageTransition } from '@/components/PageTransition';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as DatePickerCalendar } from '@/components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Clock,
  Grid3X3,
  GraduationCap,
  Shield,
  CheckSquare,
  Edit2,
  Trash2,
  X,
  Paperclip,
  MessageSquare,
  Download,
  File,
  FileText,
  Image as ImageIcon,
  FileSpreadsheet,
  FileVideo,
  Upload,
  Smile,
  Users,
  LayoutGrid,
  Umbrella,
  ListTodo
} from 'lucide-react';
import { TOOL_REGISTRY as toolConfigs } from '@/config/tools';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { format, parseISO, isSameDay, addMonths, subMonths, addWeeks, subWeeks } from 'date-fns';
import { Calendar, CalendarHeader, CalendarGrid, CalendarWeekView, EVENT_COLORS, EVENT_COLOR_OPTIONS } from '@/components/calendar';

const EVENT_TYPES = {
  milestone: { label: 'Milestone', color: '#3b82f6', bgColor: 'bg-blue-500', textColor: 'text-blue-700', calendarColor: 'blue' },
  deadline: { label: 'Deadline', color: '#ef4444', bgColor: 'bg-red-500', textColor: 'text-red-700', calendarColor: 'red' },
  meeting: { label: 'Meeting', color: '#a855f7', bgColor: 'bg-purple-500', textColor: 'text-purple-700', calendarColor: 'purple' },
  training: { label: 'Training', color: '#10b981', bgColor: 'bg-green-500', textColor: 'text-green-700', calendarColor: 'green' },
  other: { label: 'Other', color: '#6b7280', bgColor: 'bg-gray-500', textColor: 'text-gray-700', calendarColor: 'gray' }
};

// Map hex colors to calendar color keys using EVENT_COLOR_OPTIONS
const hexToCalendarColor = (hexColor) => {
  if (!hexColor) return 'blue';
  const normalizedHex = hexColor.toLowerCase();

  // First try exact match from EVENT_COLOR_OPTIONS
  const exactMatch = EVENT_COLOR_OPTIONS.find(opt => opt.hex.toLowerCase() === normalizedHex);
  if (exactMatch) return exactMatch.key;

  // Fallback color map for legacy hex values and event source colors
  const colorMap = {
    '#3b82f6': 'blue',
    '#ef4444': 'red',
    '#a855f7': 'purple',
    '#8b5cf6': 'purple',  // Cert expiries
    '#10b981': 'green',
    '#6b7280': 'gray',
    '#f59e0b': 'amber',
    '#ec4899': 'pink',
    '#f97316': 'orange',
    '#06b6d4': 'cyan',
    '#14b8a6': 'cyan'     // Leave requests (teal -> cyan)
  };
  return colorMap[normalizedHex] || 'blue';
};

const SOURCE_ICONS = {
  schedule_event: CalendarIcon,
  booking: GraduationCap,
  checklist: CheckSquare,
  compliance: Shield,
  matrix: Grid3X3,
  task: ListTodo,
  leave: Umbrella,
  holiday: CalendarIcon
};

// Event source configuration for filtering and legend
const EVENT_SOURCE_CONFIG = [
  { key: 'schedule_event', label: 'Custom Events', color: '#3B82F6', icon: CalendarIcon },
  { key: 'matrix', label: 'Cert Expiry', color: '#8B5CF6', icon: Shield },
  { key: 'booking', label: 'Training', color: '#10B981', icon: GraduationCap },
  { key: 'task', label: 'Tasks', color: '#F59E0B', icon: ListTodo },
  { key: 'leave', label: 'Leave', color: '#14B8A6', icon: Umbrella },
  { key: 'holiday', label: 'Holidays', color: '#EF4444', icon: CalendarIcon }
];

const EMOJI_REACTIONS = ['👍', '❤️', '😊', '🎉', '🚀', '👏'];

const COMMENT_EMOJIS = [
  '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊',
  '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘',
  '😗', '😙', '😚', '☺️', '😋', '😛', '😝', '😜',
  '🤪', '🤨', '🧐', '🤓', '😎', '🥳', '😏', '😒',
  '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖',
  '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡',
  '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰',
  '👍', '👎', '👊', '✊', '🤛', '🤜', '👏', '🙌',
  '👐', '🤲', '🤝', '🙏', '✌️', '🤞', '🤟', '🤘',
  '🤙', '👈', '👉', '👆', '👇', '☝️', '✋', '🤚',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
  '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖',
  '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️',
  '🎉', '🎊', '🎁', '🎈', '🎀', '🎂', '🍰', '🧁',
  '🔥', '⭐', '🌟', '✨', '💫', '🎯', '🏆', '🥇',
  '🚀', '💯', '✅', '☑️', '✔️', '❌', '❎', '⚠️'
];

export default function SchedulePageV2({ stackId, stackName, stackProjects, isStackView = false }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [globalToolsOpen, setGlobalToolsOpen] = useState(false);
  const handleGlobalToolsToggle = useCallback(() => setGlobalToolsOpen(prev => !prev), []);

  // Determine which project IDs to use
  const projectIds = useMemo(() => {
    return isStackView ? stackProjects?.map(p => p.id) : [projectId];
  }, [isStackView, stackProjects, projectId]);

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSpaceFilter, setSelectedSpaceFilter] = useState('all');
  const [calendarView, setCalendarView] = useState('month'); // 'month' | 'week' | 'agenda'

  // Navigation handlers based on current view
  const handlePrevious = useCallback(() => {
    if (calendarView === 'week') {
      setCurrentMonth(prev => subWeeks(prev, 1));
    } else {
      setCurrentMonth(prev => subMonths(prev, 1));
    }
  }, [calendarView]);

  const handleNext = useCallback(() => {
    if (calendarView === 'week') {
      setCurrentMonth(prev => addWeeks(prev, 1));
    } else {
      setCurrentMonth(prev => addMonths(prev, 1));
    }
  }, [calendarView]);

  const handleToday = useCallback(() => {
    setCurrentMonth(new Date());
  }, []);

  const handleViewChange = useCallback((newView) => {
    setCalendarView(newView);
  }, []);

  // Event source filter state - all visible by default
  const [visibleSources, setVisibleSources] = useState({
    schedule_event: true,
    matrix: true,
    booking: true,
    task: true,
    leave: true,
    holiday: true
  });

  // Dialog states
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Form states
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventDate, setEventDate] = useState(new Date());
  const [eventTime, setEventTime] = useState('');
  const [eventType, setEventType] = useState('milestone');
  const [eventColor, setEventColor] = useState('#3b82f6');

  // Comment input states
  const [newComment, setNewComment] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const [showCommentEmojiPicker, setShowCommentEmojiPicker] = useState(false);

  // Use optimized data hook
  const {
    space,
    events,
    projectMembers,
    comments,
    attachments,
    commentReactions,
    initialLoading: loading,
    fetchingEvents,
    loadingDetails: loadingComments,
    fetchEventDetails,
    refreshEventDetails,
    refetch: fetchAllEvents,
    silentRefetch
  } = useScheduleData({
    projectId,
    projectIds,
    isStackView,
    currentMonth,
    enabled: !!(projectIds && projectIds.length > 0 && projectIds[0])
  });

  // Derive spaceTools from space data
  const spaceTools = useMemo(() => {
    if (isStackView) {
      // For stack view, set up standard tools
      return [
        { id: 'matrix', tool_type: 'matrix', name: toolConfigs.matrix.name, icon: toolConfigs.matrix.icon, color: toolConfigs.matrix.color },
        { id: 'team', tool_type: 'team', name: toolConfigs.team.name, icon: toolConfigs.team.icon, color: toolConfigs.team.color },
        { id: 'compliance', tool_type: 'compliance', name: toolConfigs.compliance.name, icon: toolConfigs.compliance.icon, color: toolConfigs.compliance.color },
        { id: 'bookings', tool_type: 'bookings', name: toolConfigs.bookings.name, icon: toolConfigs.bookings.icon, color: toolConfigs.bookings.color },
        { id: 'calendar', tool_type: 'calendar', name: toolConfigs.calendar.name, icon: toolConfigs.calendar.icon, color: toolConfigs.calendar.color },
        { id: 'qualifications', tool_type: 'qualifications', name: toolConfigs.qualifications.name, icon: toolConfigs.qualifications.icon, color: toolConfigs.qualifications.color },
      ];
    }
    if (!space?.tools) return [];
    const toolsObj = typeof space.tools === 'string' ? JSON.parse(space.tools) : space.tools;
    return Object.entries(toolsObj)
      .filter(([, enabled]) => enabled === true)
      .map(([toolKey]) => ({
        id: toolKey,
        tool_type: toolKey,
        name: toolConfigs[toolKey]?.name || toolKey,
        icon: toolConfigs[toolKey]?.icon,
        color: toolConfigs[toolKey]?.color
      }));
  }, [isStackView, space?.tools]);

  // Transform and filter events for CalendarGrid component
  const calendarEvents = useMemo(() => {
    return events
      .filter(event => visibleSources[event.source] !== false)
      .map(event => ({
        ...event,
        id: event.id,
        title: event.title,
        startDate: parseISO(event.date),
        endDate: event.endDate ? parseISO(event.endDate) : undefined,
        hexColor: event.color,
        color: hexToCalendarColor(event.color) || EVENT_TYPES[event.type]?.calendarColor || 'blue'
      }));
  }, [events, visibleSources]);

  const getEventsForDate = (date) => {
    return events.filter(event => isSameDay(parseISO(event.date), date));
  };

  const handleDateClick = (date) => {
    // If clicking the same date, clear the filter
    if (selectedDate && isSameDay(date, selectedDate)) {
      setSelectedDate(null);
    } else {
      setSelectedDate(date);
    }
  };

  const handleAddEvent = () => {
    setSelectedEvent(null);
    setEventTitle('');
    setEventDescription('');
    setEventDate(selectedDate || new Date());
    setEventTime('');
    setEventType('milestone');
    setEventColor('#3b82f6');
    setShowEventDialog(true);
  };

  const handleViewEvent = useCallback(async (event) => {
    // For navigable events, navigate to the source tool instead of showing dialog
    const basePath = isStackView
      ? (event.projectId ? `/space/${event.projectId}` : '')
      : (projectId ? `/space/${projectId}` : '');

    switch (event.source) {
      case 'booking':
        // Navigate to the specific booking
        navigate(`${basePath}/bookings/${event.sourceId}`);
        return;

      case 'task':
        // Navigate to tasks page
        navigate(`${basePath}/tasks`);
        return;

      case 'leave':
        // Navigate to attendance/leave page
        navigate(`${basePath || '/attendance'}`);
        return;

      case 'matrix':
        // Certificate expiry - navigate to matrix
        navigate(`${basePath}/matrix`);
        return;

      case 'holiday':
        // For holidays, just show a simple toast with info
        toast.info(`${event.title}`, {
          description: `Bank holiday on ${format(parseISO(event.date), 'EEEE, MMMM d, yyyy')}`
        });
        return;

      case 'schedule_event':
      default:
        // For custom schedule events, show the detail dialog
        setSelectedEvent(event);
        // Populate form state for inline editing (use hexColor for the original hex value)
        setEventTitle(event.title);
        setEventDescription(event.description || '');
        setEventDate(parseISO(event.date));
        setEventTime(event.time || '');
        setEventType(event.type);
        setEventColor(event.hexColor || '#3b82f6');
        setShowEventDetail(true);

        // Fetch event details (comments, reactions, attachments) using RPC
        await fetchEventDetails(event.id, event.source);
        break;
    }
  }, [fetchEventDetails, navigate, isStackView, projectId]);

  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setEventTitle(event.title);
    setEventDescription(event.description || '');
    setEventDate(parseISO(event.date));
    setEventTime(event.time || '');
    setEventType(event.type);
    setEventColor(event.hexColor || '#3b82f6');
    setShowEventDetail(false);
    setShowEventDialog(true);
  };

  const handleSaveEvent = async () => {
    if (!eventTitle.trim()) {
      toast.error('Event title is required');
      return;
    }

    // Don't allow creating events in stack view
    if (isStackView && !selectedEvent) {
      toast.error('Cannot create events in stack view. Please select a specific space.');
      return;
    }

    try {
      if (selectedEvent) {
        // Update existing event
        const { error } = await supabase
          .from('schedule_events')
          .update({
            title: eventTitle,
            description: eventDescription,
            event_date: format(eventDate, 'yyyy-MM-dd'),
            event_time: eventTime || null,
            event_type: eventType,
            color: eventColor
          })
          .eq('id', selectedEvent.sourceId);

        if (error) throw error;
        toast.success('Event updated successfully');
      } else {
        // Create new event
        const { error } = await supabase
          .from('schedule_events')
          .insert([{
            project_id: projectId,
            title: eventTitle,
            description: eventDescription,
            event_date: format(eventDate, 'yyyy-MM-dd'),
            event_time: eventTime || null,
            event_type: eventType,
            color: eventColor,
            created_by: userProfile.id
          }]);

        if (error) throw error;
        toast.success('Event created successfully');
      }

      setShowEventDialog(false);
      silentRefetch();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error('Failed to save event');
    }
  };

  const handleDeleteEvent = async (event) => {
    if (!event.canEdit) {
      toast.error('This event cannot be deleted');
      return;
    }

    if (!confirm(`Delete "${event.title}"?`)) return;

    try {
      const { error } = await supabase
        .from('schedule_events')
        .delete()
        .eq('id', event.sourceId);

      if (error) throw error;
      toast.success('Event deleted');
      silentRefetch();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;

    setNewComment(value);
    setMentionCursorPosition(cursorPos);

    // Check if typing @ for mentions
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);
      // Only show dropdown if there's no space after @ and we're within 20 chars
      if (!textAfterAt.includes(' ') && textAfterAt.length <= 20) {
        setMentionSearch(textAfterAt.toLowerCase());
        setShowMentionDropdown(true);
        return;
      }
    }

    setShowMentionDropdown(false);
  };

  const handleSelectMention = (member) => {
    console.log('[handleSelectMention] Selected member:', member);
    console.log('[handleSelectMention] Member ID:', member.id);
    console.log('[handleSelectMention] Member name:', member.first_name, member.last_name);

    // Find the last @ before cursor
    const textBeforeCursor = newComment.substring(0, mentionCursorPosition);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol !== -1) {
      const beforeAt = newComment.substring(0, lastAtSymbol);
      const afterCursor = newComment.substring(mentionCursorPosition);

      // Use a unique mention format with user ID embedded
      const mentionText = `@${member.first_name} ${member.last_name}[${member.id}]`;
      console.log('[handleSelectMention] Generated mention text:', mentionText);
      console.log('[handleSelectMention] New comment will be:', beforeAt + mentionText + ' ' + afterCursor);

      setNewComment(beforeAt + mentionText + ' ' + afterCursor);
      setShowMentionDropdown(false);
      setMentionSearch('');
    }
  };

  const handleInsertEmoji = (emoji) => {
    // Insert emoji at current cursor position
    const textarea = document.querySelector('textarea[placeholder*="Add your comment"]');
    const cursorPos = textarea?.selectionStart || newComment.length;

    const beforeCursor = newComment.substring(0, cursorPos);
    const afterCursor = newComment.substring(cursorPos);

    setNewComment(beforeCursor + emoji + afterCursor);
    setShowCommentEmojiPicker(false);

    // Focus back on textarea after inserting emoji
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = cursorPos + emoji.length;
      }
    }, 0);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedEvent) return;

    try {
      console.log('[handleAddComment] Raw comment text:', newComment);
      console.log('[handleAddComment] Comment length:', newComment.length);

      // Extract mentioned user IDs from the comment using the unique format @Name[id]
      const mentionedUserIds = [];
      const mentionRegex = /@[\w\s]+\[([a-f0-9-]{36})\]/g;
      let match;

      while ((match = mentionRegex.exec(newComment)) !== null) {
        console.log('[handleAddComment] Regex match found:', match[0], 'userId:', match[1]);
        const userId = match[1];
        mentionedUserIds.push(userId);
      }

      console.log('[handleAddComment] Extracted mentionedUserIds:', mentionedUserIds);

      const { error } = await supabase
        .from('schedule_event_comments')
        .insert([{
          event_id: selectedEvent.id,
          event_source: selectedEvent.source,
          content: newComment.trim(),
          created_by: userProfile.id,
          mentions: mentionedUserIds.length > 0 ? mentionedUserIds : null
        }]);

      if (error) throw error;

      // Create notifications for mentioned users
      if (mentionedUserIds.length > 0) {
        console.log('[handleAddComment] userProfile.id:', userProfile.id);
        console.log('[handleAddComment] Creating notifications for users:', mentionedUserIds);

        const mentionedUserIdsFiltered = mentionedUserIds.filter(userId => {
          const shouldNotify = userId !== userProfile.id;
          console.log(`[handleAddComment] userId ${userId} === userProfile.id ${userProfile.id}? ${!shouldNotify} (will notify: ${shouldNotify})`);
          return shouldNotify;
        });

        console.log('[handleAddComment] Filtered notifications array length:', mentionedUserIdsFiltered.length);

        if (mentionedUserIdsFiltered.length > 0) {
          console.log('[handleAddComment] Creating notifications for user IDs:', mentionedUserIdsFiltered);
          await sendNotification({
            notification_type: 'mentions',
            user_ids: mentionedUserIdsFiltered,
            title: 'You were mentioned',
            message: `${userProfile.first_name} ${userProfile.last_name} mentioned you in a comment on "${selectedEvent.title}"`,
            space_id: projectId,
            link_url: `/space/${projectId}/schedule?event=${selectedEvent.id}`,
            entity_type: 'schedule_event_comment',
            entity_id: selectedEvent.sourceId, // Use sourceId (UUID) instead of composite id
            actor_id: userProfile.id,
            group_key: `event_${selectedEvent.id}_mentions`
          });
          console.log('[handleAddComment] Notifications sent successfully via helper');
        } else {
          console.log('[handleAddComment] No notifications to create (empty array after filtering)');
        }
      }

      setNewComment('');
      setShowMentionDropdown(false);
      setMentionSearch('');
      await refreshEventDetails(selectedEvent.id, selectedEvent.source);
      toast.success('Comment added');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!confirm('Delete this comment?')) return;

    try {
      const { error } = await supabase
        .from('schedule_event_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      await refreshEventDetails(selectedEvent.id, selectedEvent.source);
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!confirm(`Delete "${attachment.file_name}"?`)) return;

    try {
      // Delete from GCS
      await gcsDeleteFile(attachment.file_path);

      // Delete from database
      const { error: dbError } = await supabase
        .from('schedule_event_attachments')
        .delete()
        .eq('id', attachment.id);

      if (dbError) throw dbError;

      await refreshEventDetails(selectedEvent.id, selectedEvent.source);
      toast.success('Attachment deleted');
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment');
    }
  };

  const handleAddReaction = async (commentId, emoji) => {
    try {
      const { error } = await supabase
        .from('schedule_event_comment_reactions')
        .insert([{
          comment_id: commentId,
          user_id: userProfile.id,
          emoji: emoji
        }]);

      if (error) throw error;

      await refreshEventDetails(selectedEvent.id, selectedEvent.source);
      setShowEmojiPicker(null);
    } catch (error) {
      console.error('Error adding reaction:', error);
      toast.error('Failed to add reaction');
    }
  };

  const handleRemoveReaction = async (commentId, emoji) => {
    try {
      const { error } = await supabase
        .from('schedule_event_comment_reactions')
        .delete()
        .eq('comment_id', commentId)
        .eq('user_id', userProfile.id)
        .eq('emoji', emoji);

      if (error) throw error;

      await refreshEventDetails(selectedEvent.id, selectedEvent.source);
    } catch (error) {
      console.error('Error removing reaction:', error);
      toast.error('Failed to remove reaction');
    }
  };

  const uploadFile = async (file) => {
    if (!file || !selectedEvent) return;

    try {
      setUploadingFile(true);

      // Upload to GCS
      const fileName = `${Date.now()}_${file.name}`;
      const folderPath = `project-files/schedule-events/${projectId}/${selectedEvent.source}/${selectedEvent.id}`;

      const uploadResult = await gcsUploadFile(file, {
        folderPath,
        fileName,
        contentType: file.type,
      });

      // Save attachment metadata with polymorphic relationship
      const { error: dbError } = await supabase
        .from('schedule_event_attachments')
        .insert([{
          event_id: selectedEvent.id,
          event_source: selectedEvent.source,
          file_name: file.name,
          file_path: uploadResult.filePath,
          file_size: file.size,
          file_type: file.type,
          uploaded_by: userProfile.id
        }]);

      if (dbError) throw dbError;

      await refreshEventDetails(selectedEvent.id, selectedEvent.source);
      toast.success('File uploaded');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      await uploadFile(file);
      // Reset input
      event.target.value = '';
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const { signedUrl } = await gcsGetSignedDownloadUrl(attachment.file_path);
      const response = await fetch(signedUrl);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const getFileIcon = (fileType, fileName) => {
    if (!fileType && fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) {
        return { Icon: ImageIcon, color: 'text-purple-500', bg: 'bg-purple-50' };
      }
      if (['pdf'].includes(ext)) {
        return { Icon: FileText, color: 'text-red-500', bg: 'bg-red-50' };
      }
      if (['doc', 'docx', 'txt'].includes(ext)) {
        return { Icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' };
      }
      if (['xls', 'xlsx', 'csv'].includes(ext)) {
        return { Icon: FileSpreadsheet, color: 'text-green-500', bg: 'bg-green-50' };
      }
      if (['mp4', 'mov', 'avi', 'mkv'].includes(ext)) {
        return { Icon: FileVideo, color: 'text-orange-500', bg: 'bg-orange-50' };
      }
    }

    if (fileType?.startsWith('image/')) {
      return { Icon: ImageIcon, color: 'text-purple-500', bg: 'bg-purple-50' };
    }
    if (fileType?.includes('pdf')) {
      return { Icon: FileText, color: 'text-red-500', bg: 'bg-red-50' };
    }
    if (fileType?.includes('document') || fileType?.includes('text')) {
      return { Icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' };
    }
    if (fileType?.includes('sheet') || fileType?.includes('csv')) {
      return { Icon: FileSpreadsheet, color: 'text-green-500', bg: 'bg-green-50' };
    }
    if (fileType?.includes('video')) {
      return { Icon: FileVideo, color: 'text-orange-500', bg: 'bg-orange-50' };
    }

    return { Icon: File, color: 'text-gray-500', bg: 'bg-gray-50' };
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <PageTransition>
      <div className="bg-muted/40 h-screen flex flex-col">
        <SharedNavbar isGlobalToolsOpen={globalToolsOpen} onGlobalToolsToggle={handleGlobalToolsToggle} />
        <GlobalToolsExpandingPanel isOpen={globalToolsOpen} onClose={() => setGlobalToolsOpen(false)} currentTool="calendar" />

        {/* Space Navigation Bar / ToolHeader */}
        {!isStackView ? (
          <>
            <ToolHeader
              spaceName={space?.name || 'Space'}
              toolName="Calendar"
              currentTool="calendar"
              spaceTools={spaceTools}
            />
            {/* Calendar Header with Untitled UI design */}
            <div className="bg-white border-b px-6">
              <CalendarHeader
                currentDate={currentMonth}
                view={calendarView}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onToday={handleToday}
                onViewChange={handleViewChange}
                onAddEvent={handleAddEvent}
                showAddButton={true}
                filterLegend={
                  <div className="flex items-center gap-2 border-r pr-3 mr-1">
                    {EVENT_SOURCE_CONFIG.map(source => {
                      const isActive = visibleSources[source.key];
                      const isCustomEvents = source.key === 'schedule_event';
                      return (
                        <button
                          key={source.key}
                          onClick={() => setVisibleSources(prev => ({ ...prev, [source.key]: !prev[source.key] }))}
                          className="flex items-center gap-1.5 text-xs transition-all hover:opacity-80"
                        >
                          <div
                            className="w-2.5 h-2.5 rounded-full transition-all"
                            style={{
                              background: isCustomEvents
                                ? (isActive ? 'conic-gradient(#3b82f6, #ec4899, #f59e0b, #10b981, #8b5cf6, #3b82f6)' : 'transparent')
                                : (isActive ? source.color : 'transparent'),
                              border: isCustomEvents
                                ? '2px solid #9ca3af'
                                : `2px solid ${source.color}`,
                              opacity: isActive ? 1 : 0.5
                            }}
                          />
                          <span className={isActive ? 'text-gray-600 font-medium' : 'text-gray-400'}>
                            {source.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                }
              />
            </div>
          </>
        ) : (
          <div className="w-full bg-white border-b border-gray-200">
            <div className="flex items-center justify-between py-3 px-6">
              {/* Left side - Tool switcher + Breadcrumb */}
              <div className="flex items-center gap-3">
                {/* Tools Dropdown */}
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 px-2">
                      <LayoutGrid className="h-4 w-4 text-gray-600" />
                      <ChevronDown className="h-3 w-3 text-gray-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[400px] p-4">
                    <div className="grid grid-cols-3 gap-3">
                      {spaceTools.map((tool) => {
                        const Icon = tool.icon;
                        const isActive = tool.id === 'calendar';

                        // Skip tools without valid icons
                        // Accept both function components and React.forwardRef components
                        if (!Icon) {
                          console.warn('[SchedulePageV2] No icon provided for tool:', tool.id);
                          return null;
                        }

                        // Validate it's a renderable React component (function or forwardRef)
                        const isValidComponent = typeof Icon === 'function' || (typeof Icon === 'object' && Icon.$$typeof);
                        if (!isValidComponent) {
                          console.warn('[SchedulePageV2] Invalid icon for tool:', tool.id, Icon);
                          return null;
                        }

                        return (
                          <div
                            key={tool.id}
                            onClick={() => {
                              navigate(`/stack/${stackId}/${tool.id === 'calendar' ? 'schedule' : tool.id}`);
                            }}
                            className={`flex flex-col items-center p-4 rounded-lg border cursor-pointer transition-all ${
                              isActive
                                ? 'border-gray-300 bg-gray-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center mb-2"
                              style={{ backgroundColor: tool.color + '15' }}
                            >
                              <Icon className="h-5 w-5" style={{ color: tool.color }} />
                            </div>
                            <span className="text-xs font-medium text-gray-700 text-center">
                              {tool.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Breadcrumb: Stack Name / Calendar */}
                <div className="flex items-center gap-2.5">
                  <button
                    onClick={() => navigate(`/stack/${stackId}`)}
                    className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {stackName}
                  </button>
                  <span className="text-sm text-gray-300">/</span>
                  <span className="text-sm font-semibold text-gray-700">Calendar</span>
                </div>
              </div>

              {/* Right side - Space Filter */}
              <div className="flex items-center gap-3">
                {/* Space Filter Dropdown - Only for stack view */}
                {stackProjects && (
                  <Select value={selectedSpaceFilter} onValueChange={setSelectedSpaceFilter}>
                    <SelectTrigger className="w-[200px] h-8 text-sm">
                      <SelectValue placeholder="Filter by space" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Spaces ({stackProjects.length})</SelectItem>
                      {stackProjects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            {/* Calendar Header with Untitled UI design */}
            <div className="px-6 border-t">
              <CalendarHeader
                currentDate={currentMonth}
                view={calendarView}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onToday={handleToday}
                onViewChange={handleViewChange}
                showAddButton={false}
                filterLegend={
                  <div className="flex items-center gap-2 border-r pr-3 mr-1">
                    {EVENT_SOURCE_CONFIG.map(source => {
                      const isActive = visibleSources[source.key];
                      const isCustomEvents = source.key === 'schedule_event';
                      return (
                        <button
                          key={source.key}
                          onClick={() => setVisibleSources(prev => ({ ...prev, [source.key]: !prev[source.key] }))}
                          className="flex items-center gap-1.5 text-xs transition-all hover:opacity-80"
                        >
                          <div
                            className="w-2.5 h-2.5 rounded-full transition-all"
                            style={{
                              background: isCustomEvents
                                ? (isActive ? 'conic-gradient(#3b82f6, #ec4899, #f59e0b, #10b981, #8b5cf6, #3b82f6)' : 'transparent')
                                : (isActive ? source.color : 'transparent'),
                              border: isCustomEvents
                                ? '2px solid #9ca3af'
                                : `2px solid ${source.color}`,
                              opacity: isActive ? 1 : 0.5
                            }}
                          />
                          <span className={isActive ? 'text-gray-600 font-medium' : 'text-gray-400'}>
                            {source.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                }
              />
            </div>
          </div>
        )}

      {/* Calendar Grid - Full height with independent scrolls */}
      <div className="flex-1 flex overflow-hidden">
          {/* Main Calendar - Independent scroll */}
          <div className="flex-1 flex flex-col overflow-hidden border-r relative">
            {/* Loading Overlay */}
            {fetchingEvents && (
              <div className="absolute inset-0 flex items-center justify-center z-20 bg-gray-50/80 backdrop-blur-sm">
                <div className="flex flex-col items-center gap-3">
                  <LoadingSpinner />
                  <span className="text-sm text-gray-600 font-medium">Loading {format(currentMonth, 'MMMM yyyy')}...</span>
                </div>
              </div>
            )}
            <div className={`flex-1 p-6 overflow-y-auto transition-opacity duration-300 ${fetchingEvents ? 'opacity-30' : 'opacity-100'}`}>
              {calendarView === 'month' && (
                <CalendarGrid
                  currentDate={currentMonth}
                  selectedDate={selectedDate}
                  events={calendarEvents}
                  onDateClick={handleDateClick}
                  onEventClick={handleViewEvent}
                  onOverflowClick={(date) => {
                    setSelectedDate(date);
                  }}
                  weekStartsOnMonday={false}
                />
              )}
              {calendarView === 'week' && (
                <CalendarWeekView
                  currentDate={currentMonth}
                  selectedDate={selectedDate}
                  events={calendarEvents}
                  onDateClick={handleDateClick}
                  onEventClick={handleViewEvent}
                  weekStartsOnMonday={false}
                />
              )}
            </div>
          </div>

          {/* Events List Sidebar */}
          <div className="w-72 bg-gray-50/50 flex flex-col overflow-hidden">
            <div className={`flex-1 overflow-y-auto transition-opacity duration-300 ${fetchingEvents ? 'opacity-50' : 'opacity-100'}`}>
              {/* Header */}
              <div className="p-4 border-b bg-white sticky top-0 z-10">
                {selectedDate ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm">
                        {format(selectedDate, 'EEEE, MMM d')}
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {selectedDateEvents.length} {selectedDateEvents.length === 1 ? 'event' : 'events'}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedDate(null)}
                      className="h-7 w-7 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">
                      Upcoming Events
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {events.length} in {format(currentMonth, 'MMMM')}
                    </p>
                  </div>
                )}
              </div>

              {/* Events List */}
              <div className="p-3">
                {(selectedDate ? selectedDateEvents : events).length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                      <CalendarIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-600">No events</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedDate ? 'Nothing scheduled for this date' : 'Nothing scheduled this month'}
                    </p>
                  </div>
                ) : selectedDate ? (
                  <div className="space-y-2">
                    {selectedDateEvents.map(event => {
                      return (
                        <div
                          key={event.id}
                          className="p-3 rounded-lg bg-white border border-gray-200 cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all"
                          onClick={() => handleViewEvent(event)}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                              style={{ backgroundColor: event.color || '#3B82F6' }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 leading-tight">
                                {event.title}
                              </p>
                              <div className="flex items-center gap-2 mt-1.5">
                                {event.time && (
                                  <span className="text-xs text-gray-500">{event.time}</span>
                                )}
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                                  {EVENT_TYPES[event.type]?.label || 'Event'}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Array.from(new Set(events.map(e => e.date)))
                      .sort()
                      .map(date => {
                        const dateEvents = events.filter(e => e.date === date);
                        const parsedDate = parseISO(date);
                        const isToday = isSameDay(parsedDate, new Date());

                        return (
                          <div key={date}>
                            <button
                              className="flex items-center gap-2 mb-2 group"
                              onClick={() => handleDateClick(parsedDate)}
                            >
                              <span className={`text-xs font-medium ${isToday ? 'text-blue-600' : 'text-gray-500'} group-hover:text-blue-600 transition-colors`}>
                                {format(parsedDate, 'EEE, MMM d')}
                              </span>
                              {isToday && (
                                <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Today</span>
                              )}
                            </button>

                            <div className="space-y-1.5">
                              {dateEvents.map(event => {
                                return (
                                  <div
                                    key={event.id}
                                    className="flex items-center gap-2.5 p-2 rounded-md bg-white border border-gray-100 cursor-pointer hover:border-gray-200 hover:bg-gray-50 transition-all"
                                    onClick={() => handleViewEvent(event)}
                                  >
                                    <div
                                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: event.color || '#3B82F6' }}
                                    />
                                    <p className="text-xs font-medium text-gray-700 truncate flex-1">
                                      {event.title}
                                    </p>
                                    {event.time && (
                                      <span className="text-[10px] text-gray-400 flex-shrink-0">{event.time}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      {/* Event Detail Dialog - Tabbed Interface */}
      <Dialog open={showEventDetail} onOpenChange={setShowEventDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="pb-0">
            <DialogTitle className="flex items-center gap-3">
              {selectedEvent && (
                <>
                  <span
                    className={`w-3 h-3 rounded-full ${
                      EVENT_COLOR_OPTIONS.find(c => c.hex.toLowerCase() === (selectedEvent.hexColor || selectedEvent.color)?.toLowerCase())?.bgClass || 'bg-blue-500'
                    }`}
                  />
                  {selectedEvent.title}
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedEvent && (
            <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details" className="gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Details
                </TabsTrigger>
                <TabsTrigger value="attachments" className="gap-2">
                  <Paperclip className="h-4 w-4" />
                  Attachments
                  {attachments.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{attachments.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="comments" className="gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Comments
                  {comments.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{comments.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Details Tab - Editable Form */}
              <TabsContent value="details" className="flex-1 overflow-y-auto mt-4">
                {selectedEvent.canEdit ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Title *</Label>
                      <Input
                        value={eventTitle}
                        onChange={(e) => setEventTitle(e.target.value)}
                        placeholder="Event title"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={eventDescription}
                        onChange={(e) => setEventDescription(e.target.value)}
                        placeholder="Event description"
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1.5">
                        <Label>Date *</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between font-normal">
                              {format(eventDate, 'PPP')}
                              <CalendarIcon className="h-4 w-4 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <DatePickerCalendar
                              mode="single"
                              selected={eventDate}
                              onSelect={(date) => date && setEventDate(date)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label>Time</Label>
                        <Input
                          type="time"
                          value={eventTime}
                          onChange={(e) => setEventTime(e.target.value)}
                          className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden"
                        />
                      </div>
                    </div>
                    <div className="flex gap-6 items-end">
                      <div className="flex flex-col">
                        <Label>Colour</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className={`h-9 w-9 rounded-md border border-input shadow-sm hover:bg-accent transition-colors ${
                                EVENT_COLOR_OPTIONS.find(c => c.hex.toLowerCase() === eventColor.toLowerCase())?.bgClass || 'bg-blue-500'
                              }`}
                            />
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-2" align="start">
                            <div className="grid grid-cols-5 gap-1.5">
                              {EVENT_COLOR_OPTIONS.map((colorOpt) => (
                                <button
                                  key={colorOpt.key}
                                  type="button"
                                  onClick={() => setEventColor(colorOpt.hex)}
                                  className={`w-7 h-7 rounded-md ${colorOpt.bgClass} transition-all hover:scale-110 ${
                                    eventColor.toLowerCase() === colorOpt.hex.toLowerCase() ? 'ring-2 ring-offset-1 ring-gray-900' : ''
                                  }`}
                                />
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="flex-1">
                        <Label>Type</Label>
                        <Select value={eventType} onValueChange={setEventType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(EVENT_TYPES).map(([key, config]) => (
                              <SelectItem key={key} value={key}>{config.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Action Buttons - Delete far left, Cancel/Save right */}
                    <div className="flex justify-between pt-4 mt-2 border-t">
                      <Button
                        variant="outline"
                        onClick={() => {
                          handleDeleteEvent(selectedEvent);
                          setShowEventDetail(false);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setShowEventDetail(false)}>
                          Cancel
                        </Button>
                        <Button onClick={async () => {
                          await handleSaveEvent();
                          setShowEventDetail(false);
                        }}>
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Read-only view for non-editable events */
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-500 text-xs font-medium">Title</Label>
                      <p className="mt-1 text-sm text-gray-900">{selectedEvent.title}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-500 text-xs font-medium">Date</Label>
                        <div className="mt-1 flex items-center gap-2 text-sm text-gray-900">
                          <CalendarIcon className="h-4 w-4 text-gray-400" />
                          {format(parseISO(selectedEvent.date), 'EEEE, MMMM d, yyyy')}
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-500 text-xs font-medium">Time</Label>
                        <div className="mt-1 flex items-center gap-2 text-sm text-gray-900">
                          <Clock className="h-4 w-4 text-gray-400" />
                          {selectedEvent.time || 'All day'}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-500 text-xs font-medium">Type</Label>
                        <div className="mt-1">
                          <Badge variant="secondary">
                            {EVENT_TYPES[selectedEvent.type]?.label || 'Event'}
                          </Badge>
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-500 text-xs font-medium">Source</Label>
                        <div className="mt-1">
                          <Badge variant="outline">
                            {selectedEvent.source === 'schedule_event' ? 'Custom Event' :
                             selectedEvent.source === 'booking' ? 'Training Booking' :
                             selectedEvent.source === 'matrix' ? 'Certificate Expiry' :
                             selectedEvent.source === 'compliance' ? 'Compliance' :
                             selectedEvent.source === 'checklist' ? 'Checklist' : 'Event'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end pt-4 mt-2 border-t">
                      <Button variant="outline" onClick={() => setShowEventDetail(false)}>
                        Close
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Attachments Tab */}
              <TabsContent value="attachments" className="flex-1 overflow-y-auto mt-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      {attachments.length === 0 ? 'No attachments yet' : `${attachments.length} attachment${attachments.length !== 1 ? 's' : ''}`}
                    </p>
                    <div>
                      <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={uploadingFile}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={uploadingFile}
                        className="gap-2"
                        onClick={() => document.getElementById('file-upload').click()}
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {uploadingFile ? 'Uploading...' : 'Upload File'}
                      </Button>
                    </div>
                  </div>

                  {attachments.length > 0 ? (
                    <div className="grid gap-3">
                      {attachments.map((attachment) => {
                        const { Icon, color, bg } = getFileIcon(attachment.file_type, attachment.file_name);
                        return (
                          <div
                            key={attachment.id}
                            className="group relative border border-gray-200 rounded-lg p-3 hover:border-gray-300 hover:shadow-sm transition-all"
                          >
                            <div className="flex items-center gap-3 cursor-pointer" onClick={() => handleDownloadAttachment(attachment)}>
                              <div className={`${bg} rounded-lg p-2.5 flex-shrink-0`}>
                                <Icon className={`h-5 w-5 ${color}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {attachment.file_name}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <span>{formatFileSize(attachment.file_size)}</span>
                                  <span>•</span>
                                  <span>{attachment.user_profiles?.first_name} {attachment.user_profiles?.last_name}</span>
                                  {attachment.created_date && (
                                    <>
                                      <span>•</span>
                                      <span>{format(parseISO(attachment.created_date), 'MMM d, yyyy')}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleDownloadAttachment(attachment); }}>
                                  <Download className="h-4 w-4" />
                                </Button>
                                {attachment.uploaded_by === userProfile.id && (
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); handleDeleteAttachment(attachment); }}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
                        isDragging
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => document.getElementById('file-upload').click()}
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div className="flex flex-col items-center gap-2 pointer-events-none">
                        <div className={`rounded-full p-4 ${isDragging ? 'bg-blue-100' : 'bg-gray-100'}`}>
                          <Paperclip className={`h-8 w-8 ${isDragging ? 'text-blue-600' : 'text-gray-400'}`} />
                        </div>
                        <p className={`text-sm font-medium ${isDragging ? 'text-blue-600' : 'text-gray-600'}`}>
                          {isDragging ? 'Drop file here' : 'Drop files here or click to upload'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Supports all file types
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Comments Tab */}
              <TabsContent value="comments" className="flex-1 flex flex-col min-h-0 mt-4">
                <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
                  {loadingComments ? (
                    <div className="flex justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : comments.length > 0 ? (
                    <div className="space-y-4">
                      {comments.map((comment) => {
                        const reactions = commentReactions[comment.id] || [];
                        const reactionGroups = reactions.reduce((acc, reaction) => {
                          if (!acc[reaction.emoji]) {
                            acc[reaction.emoji] = { emoji: reaction.emoji, count: 0, users: [], hasUserReacted: false };
                          }
                          acc[reaction.emoji].count++;
                          acc[reaction.emoji].users.push(reaction.user_id);
                          if (reaction.user_id === userProfile.id) acc[reaction.emoji].hasUserReacted = true;
                          return acc;
                        }, {});

                        return (
                          <div key={comment.id} className="group relative">
                            <div className="flex gap-3">
                              {comment.user_profiles?.avatar_url ? (
                                <img src={comment.user_profiles.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-1" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0 mt-1">
                                  <span className="text-xs font-semibold text-white">{comment.user_profiles?.first_name?.[0]}{comment.user_profiles?.last_name?.[0]}</span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 mb-1">
                                  <span className="text-sm font-semibold text-gray-900">{comment.user_profiles?.first_name} {comment.user_profiles?.last_name}</span>
                                  <span className="text-xs text-gray-500">{format(parseISO(comment.created_date), 'MMM d \'at\' h:mm a')}</span>
                                </div>
                                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                  {(() => {
                                    const content = comment.content;
                                    const mentions = [];
                                    const mentionRegex = /@([\w\s]+)\[([a-f0-9-]{36})\]/g;
                                    let match;
                                    while ((match = mentionRegex.exec(content)) !== null) {
                                      mentions.push({ start: match.index, end: match.index + match[0].length, text: `@${match[1]}`, userId: match[2] });
                                    }
                                    if (mentions.length === 0) return content;
                                    mentions.sort((a, b) => a.start - b.start);
                                    const parts = [];
                                    let lastIndex = 0;
                                    mentions.forEach((mention) => {
                                      if (mention.start < lastIndex) return;
                                      if (mention.start > lastIndex) parts.push({ type: 'text', content: content.substring(lastIndex, mention.start) });
                                      parts.push({ type: 'mention', content: mention.text, userId: mention.userId });
                                      lastIndex = mention.end;
                                    });
                                    if (lastIndex < content.length) parts.push({ type: 'text', content: content.substring(lastIndex) });
                                    return parts.map((part, index) => {
                                      if (part.type === 'mention') {
                                        return <button key={`mention-${index}`} onClick={() => navigate(`/profile/${part.userId}`)} className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors font-medium cursor-pointer">{part.content}</button>;
                                      }
                                      return <span key={`text-${index}`}>{part.content}</span>;
                                    });
                                  })()}
                                </div>
                                {Object.keys(reactionGroups).length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {Object.values(reactionGroups).map((group) => (
                                      <button key={group.emoji} onClick={() => group.hasUserReacted ? handleRemoveReaction(comment.id, group.emoji) : handleAddReaction(comment.id, group.emoji)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${group.hasUserReacted ? 'bg-blue-100 border border-blue-300 hover:bg-blue-200' : 'bg-gray-100 border border-gray-200 hover:bg-gray-200'}`}>
                                        <span>{group.emoji}</span>
                                        <span className="font-medium">{group.count}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  <div className="relative">
                                    <button onClick={() => setShowEmojiPicker(showEmojiPicker === comment.id ? null : comment.id)} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100">
                                      <Smile className="h-3.5 w-3.5" />
                                    </button>
                                    {showEmojiPicker === comment.id && (
                                      <div className="absolute left-0 top-full mt-1 z-10 bg-white rounded-lg shadow-lg border border-gray-200 p-2 flex gap-1">
                                        {EMOJI_REACTIONS.map((emoji) => (
                                          <button key={emoji} onClick={() => handleAddReaction(comment.id, emoji)} className="hover:bg-gray-100 rounded p-1.5 transition-colors text-lg">{emoji}</button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  {comment.created_by === userProfile.id && (
                                    <button onClick={() => handleDeleteComment(comment.id)} className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100">
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <MessageSquare className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                      <p className="text-sm font-medium text-gray-600">No comments yet</p>
                      <p className="text-xs text-gray-500 mt-1">Start the discussion below</p>
                    </div>
                  )}
                </div>

                {/* Add Comment Input */}
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 mt-4 flex-shrink-0">
                  <div className="flex gap-3 items-start">
                    {userProfile?.avatar_url ? (
                      <img src={userProfile.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-semibold text-white">{userProfile?.first_name?.[0]}{userProfile?.last_name?.[0]}</span>
                      </div>
                    )}
                    <div className="flex-1 relative">
                      <Textarea
                        placeholder="Add your comment... (Type @ to mention someone)"
                        value={newComment}
                        onChange={handleCommentChange}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleAddComment(); }
                          if (e.key === 'Escape' && showMentionDropdown) { e.preventDefault(); setShowMentionDropdown(false); }
                        }}
                        className="min-h-[60px] resize-none bg-white"
                        rows={2}
                      />
                      {showMentionDropdown && projectMembers.length > 0 && (
                        <div className="absolute left-0 bottom-full mb-1 z-20 bg-white rounded-lg shadow-lg border border-gray-200 max-h-48 overflow-y-auto w-64">
                          {projectMembers.filter(member => { const fullName = `${member.first_name} ${member.last_name}`.toLowerCase(); return mentionSearch === '' || fullName.includes(mentionSearch); }).slice(0, 8).map(member => (
                            <button key={member.id} onClick={() => handleSelectMention(member)} className="w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors text-left">
                              {member.avatar_url ? <img src={member.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" /> : <div className="w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0"><span className="text-xs font-semibold text-white">{member.first_name?.[0]}{member.last_name?.[0]}</span></div>}
                              <span className="text-sm font-medium text-gray-900">{member.first_name} {member.last_name}</span>
                            </button>
                          ))}
                          {projectMembers.filter(member => { const fullName = `${member.first_name} ${member.last_name}`.toLowerCase(); return mentionSearch === '' || fullName.includes(mentionSearch); }).length === 0 && <div className="px-3 py-2 text-sm text-gray-500">No members found</div>}
                        </div>
                      )}
                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <button type="button" onClick={() => setShowCommentEmojiPicker(!showCommentEmojiPicker)} className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded hover:bg-gray-100" title="Add emoji">
                              <Smile className="h-4 w-4" />
                            </button>
                            {showCommentEmojiPicker && (
                              <div className="absolute left-0 bottom-full mb-2 z-30 bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-72 max-h-64 overflow-y-auto">
                                <div className="grid grid-cols-8 gap-1">
                                  {COMMENT_EMOJIS.map((emoji) => <button key={emoji} type="button" onClick={() => handleInsertEmoji(emoji)} className="hover:bg-gray-100 rounded p-1.5 transition-colors text-xl flex items-center justify-center" title={emoji}>{emoji}</button>)}
                                </div>
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">Press {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'} + Enter to post</span>
                        </div>
                        <Button size="sm" onClick={handleAddComment} disabled={!newComment.trim()}>Post</Button>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Event Edit Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedEvent ? 'Edit Event' : 'Add Event'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
                placeholder="Event title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={eventDescription}
                onChange={(e) => setEventDescription(e.target.value)}
                placeholder="Event description"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between font-normal"
                    >
                      {format(eventDate, 'PPP')}
                      <CalendarIcon className="h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <DatePickerCalendar
                      mode="single"
                      selected={eventDate}
                      onSelect={(date) => date && setEventDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={eventTime}
                  onChange={(e) => setEventTime(e.target.value)}
                  className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
              </div>
            </div>
            <div className="flex gap-6 items-end">
              <div className="flex flex-col">
                <Label>Colour</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={`h-9 w-9 rounded-md border border-input shadow-sm hover:bg-accent transition-colors ${
                        EVENT_COLOR_OPTIONS.find(c => c.hex.toLowerCase() === eventColor.toLowerCase())?.bgClass || 'bg-blue-500'
                      }`}
                      title={EVENT_COLOR_OPTIONS.find(c => c.hex.toLowerCase() === eventColor.toLowerCase())?.label || 'Select colour'}
                    />
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-2" align="start">
                    <div className="grid grid-cols-5 gap-1.5">
                      {EVENT_COLOR_OPTIONS.map((colorOpt) => (
                        <button
                          key={colorOpt.key}
                          type="button"
                          onClick={() => setEventColor(colorOpt.hex)}
                          className={`w-7 h-7 rounded-md ${colorOpt.bgClass} transition-all hover:scale-110 ${
                            eventColor.toLowerCase() === colorOpt.hex.toLowerCase()
                              ? 'ring-2 ring-offset-1 ring-gray-900'
                              : ''
                          }`}
                          title={colorOpt.label}
                        />
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex-1">
                <Label>Type</Label>
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EVENT_TYPES).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEventDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSaveEvent} className="flex-1">
                {selectedEvent ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </PageTransition>
  );
}
